import { useState } from 'react';
import { api, ApiError } from '../api';
import { dateTime } from '../format';
import type { League } from '../types';

interface Props {
  leagues: League[];
  selectedId: number | null;
  onSelect: (id: number) => void;
  onIngested: (leagueId: number) => void;
}

const EXAMPLE_URL = 'https://www.2k-dart-software.com/frontend/events/10/event/99/statistics/statistics';

/**
 * League picker plus the ingest form. A deep ingest pulls every fixture and
 * individual game, which is what the head-to-head calibration needs.
 */
export function LeagueBar({ leagues, selectedId, onSelect, onIngested }: Props) {
  const [url, setUrl] = useState('');
  const [deep, setDeep] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  const league = leagues.find((l) => l.id === selectedId) ?? null;

  const ingest = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!url.trim() || busy) return;
    setBusy(true);
    setMessage(null);
    try {
      const result = await api.ingest(url.trim(), deep);
      const { counts } = result;
      setMessage({
        kind: 'ok',
        text:
          `Imported ${result.league.name} (${result.league.season ?? 'no season'}) — ` +
          `${counts.teams} teams, ${counts.players} players, ${counts.games} games.`,
      });
      setUrl('');
      onIngested(result.leagueId);
    } catch (error) {
      setMessage({
        kind: 'error',
        text: error instanceof ApiError ? error.message : String(error),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card">
      <header>
        <h2>League</h2>
        {league && (
          <p className="hint">
            {league.organizer}
            {league.region ? ` · ${league.region}` : ''}
            {league.season ? ` · ${league.season}` : ''} · {league.teamCount} teams ·{' '}
            {league.playerCount} players · {league.gameCount} games · imported{' '}
            {dateTime(league.lastIngestedAt)}
          </p>
        )}
      </header>

      <div className="row" style={{ marginBottom: 14 }}>
        <select
          value={selectedId ?? ''}
          onChange={(e) => onSelect(Number(e.target.value))}
          disabled={leagues.length === 0}
          aria-label="Select a league"
        >
          {leagues.length === 0 && <option value="">No leagues imported yet</option>}
          {leagues.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name}
              {l.season ? ` — ${l.season}` : ''}
              {l.region ? ` (${l.region})` : ''}
            </option>
          ))}
        </select>
        {league && (
          <a className="hint" href={league.sourceUrl} target="_blank" rel="noreferrer">
            open on 3K-Dart-Software ↗
          </a>
        )}
      </div>

      <form className="row" onSubmit={ingest}>
        <input
          type="url"
          value={url}
          placeholder={EXAMPLE_URL}
          onChange={(e) => setUrl(e.target.value)}
          aria-label="League URL to import"
        />
        <label className="check" title="Also import every matchday, fixture and individual game">
          <input type="checkbox" checked={deep} onChange={(e) => setDeep(e.target.checked)} />
          full match data
        </label>
        <button className="btn primary" type="submit" disabled={busy || url.trim().length === 0}>
          {busy ? 'Importing…' : 'Import league'}
        </button>
      </form>
      <p className="hint" style={{ marginTop: 8 }}>
        Paste any tab of a league — statistics, table or match plan. A full import walks every
        matchday and can take a couple of minutes.
      </p>

      {message && (
        <div className={`notice ${message.kind === 'error' ? 'error' : 'ok'}`} style={{ marginTop: 12 }}>
          {message.text}
        </div>
      )}
    </section>
  );
}
